const QUICK_SHOP_DRAWER_FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function ensureQuickShopInertElems() {
  if (!window.inertElems?.length) {
    window.inertElems = document.querySelectorAll('[data-js-inert]');
  }
}

function lockQuickShopPageInert() {
  ensureQuickShopInertElems();
  window.inertElems.forEach((elm) => elm.setAttribute('inert', ''));
}

function unlockQuickShopPageInert() {
  if (
    (window.openedSidebars?.length ?? 0) > 0 ||
    document.querySelector('custom-guide.active') ||
    document.querySelector('product-modal.active') ||
    document.querySelector('custom-shop-the-look.active')
  ) {
    return;
  }

  ensureQuickShopInertElems();
  window.inertElems.forEach((elm) => elm.removeAttribute('inert'));
}

function getQuickShopDrawerFocusable(container) {
  if (!container) return [];

  const candidates = [...container.querySelectorAll(QUICK_SHOP_DRAWER_FOCUSABLE)].filter(
    (el) =>
      !el.hasAttribute('disabled') &&
      el.getAttribute('type') !== 'hidden' &&
      !el.closest('[hidden], [inert]')
  );

  const radioGroups = new Map();

  candidates.forEach((el) => {
    if (el.type !== 'radio' || !el.name) return;

    const group = radioGroups.get(el.name) || { first: el, checked: null, active: null };
    if (el.checked) group.checked = el;
    if (el === document.activeElement) group.active = el;
    radioGroups.set(el.name, group);
  });

  return candidates.filter((el) => {
    if (el.type !== 'radio' || !el.name) return true;

    const group = radioGroups.get(el.name);
    return el === (group.active || group.checked || group.first);
  });
}

function trapTabInQuickShopDrawer(event, container) {
  if (event.key !== 'Tab') return false;

  const focusable = getQuickShopDrawerFocusable(container);
  if (!focusable.length) return false;

  const activeIndex = focusable.indexOf(document.activeElement);

  const nextIndex =
    activeIndex === -1
      ? event.shiftKey
        ? focusable.length - 1
        : 0
      : event.shiftKey
        ? activeIndex - 1
        : activeIndex + 1;
  const target = focusable[(nextIndex + focusable.length) % focusable.length];

  event.preventDefault();
  target.focus();
  return true;
}

function focusFirstInQuickShopDrawer(container) {
  const title = container?.querySelector('#product-modal-title');
  if (title?.textContent?.trim()) {
    title.focus({ preventScroll: true });
    return;
  }

  const focusable = getQuickShopDrawerFocusable(container);
  (focusable[0] || container)?.focus({ preventScroll: true });
}

function scrollQuickShopGallerySlideIntoView(track, slide) {
  const isHorizontal = track.scrollWidth > track.clientWidth;
  track.scrollTo({
    left: isHorizontal ? slide.offsetLeft - track.offsetLeft : track.scrollLeft,
    top: isHorizontal ? track.scrollTop : slide.offsetTop - track.offsetTop,
    behavior: 'smooth',
  });
}

function updateQuickShopGalleryStatus(track, slide) {
  const status = track.closest('.quick-shop-gallery')?.querySelector('[data-gallery-status]');
  const label = slide?.dataset?.galleryStatus;
  if (status && label) status.textContent = label;
}

function getQuickShopGallerySlides(track) {
  const slides = [...track.querySelectorAll('.product-gallery-item')].filter(
    (el) => !el.closest('[inert], [aria-hidden="true"]') && el.getClientRects().length > 0
  );

  return slides;
}

function getQuickShopGalleryCurrentIndex(track, slides) {
  const isHorizontal = track.scrollWidth > track.clientWidth;
  const position = isHorizontal ? track.scrollLeft : track.scrollTop;
  const offset = isHorizontal ? track.offsetLeft : track.offsetTop;

  return slides.reduce((closestIndex, slide, index) => {
    const slidePosition = (isHorizontal ? slide.offsetLeft : slide.offsetTop) - offset;
    const closestPosition =
      (isHorizontal ? slides[closestIndex].offsetLeft : slides[closestIndex].offsetTop) - offset;

    return Math.abs(slidePosition - position) < Math.abs(closestPosition - position)
      ? index
      : closestIndex;
  }, 0);
}

function moveQuickShopGallery(track, direction) {
  const slides = getQuickShopGallerySlides(track);
  if (!slides.length) return false;

  const currentIndex = getQuickShopGalleryCurrentIndex(track, slides);
  const nextIndex = Math.max(0, Math.min(currentIndex + direction, slides.length - 1));

  if (nextIndex === currentIndex) {
    return false;
  }

  scrollQuickShopGallerySlideIntoView(track, slides[nextIndex]);
  updateQuickShopGalleryStatus(track, slides[nextIndex]);
  return true;
}

function handleQuickShopGalleryKey(event) {
  const gallery = event.target.closest?.('.quick-shop-gallery');
  if (!gallery || document.activeElement !== gallery) return false;

  const track = gallery.querySelector('.quick-shop-gallery__slides');
  if (!track) return false;

  if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
    event.preventDefault();
    return moveQuickShopGallery(track, 1);
  }

  if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
    event.preventDefault();
    return moveQuickShopGallery(track, -1);
  }

  return false;
}

function isQuickShopCoveredByDrawer() {
  return !!(
    document.querySelector('custom-guide.active') ||
    document.querySelector('custom-shop-the-look.active')
  );
}

if (!customElements.get('product-modal-toggle')) {
  class ProductModalToggle extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('click', (e) => {
        e.preventDefault();
        const modal = document.querySelector('product-modal');
        if (modal) modal.init(this);
      });
    }
  }

  customElements.define('product-modal-toggle', ProductModalToggle);
}

if (!customElements.get('product-modal')) {
  class ProductModal extends HTMLElement {
    constructor() {
      super();
      this.CACHE = new Map();
      this.CACHE_LIMIT = 10;

      this._onVariantChanged = (e) => this.handleVariantChanged(e);
      this._onCombinedListingChanged = (e) => this.handleCombinedListingChanged(e);
      this._onAddToCart = () => this.handleAddToCart();

      this.wrapper = this.querySelector('pmd-wrapper');
      this.wrapperTitle = this.querySelector('#product-modal-title');
      this.content = this.querySelector('pmd-content');
      this.close = this.querySelector('.sidebar__close');
      this.loader = this.querySelector('.button--loader');
      this.toggleElement = null;

      this._onKeyDown = this._onKeyDown.bind(this);
      document.addEventListener('keydown', this._onKeyDown);

      this.addEventListener('click', (e) => {
        if (e.target === this && e.target.classList.contains('active')) this.hide();
      });
      this.close?.addEventListener('click', () => this.hide());
    }

    // -- Lifecycle --

    init(toggleElement) {
      this.toggleElement = toggleElement;
      this.url = toggleElement.dataset.productUrl || this.url;
      this.section = toggleElement.dataset.sectionId || this.section;
      this.trackingId = toggleElement.dataset.trackingId || this.trackingId;
      this.title = toggleElement.dataset.title || null;
      this.url = new URL(this.url, window.location.origin);
      this.url.searchParams.set('sections', this.section);
      this.show();
    }

    show() {
      document.body.classList.add('modal-open');
      this.loader?.classList.add('working');
      this.wrapper?.setAttribute('aria-hidden', 'false');
      lockQuickShopPageInert();

      const loaderText = this.loader?.querySelector('.button__preloader-text');
      if (loaderText) loaderText.textContent = `Loading your next pair of ${this.title}...`;

      this.classList.add('active');
      this.style.display = '';
      this.setAttribute('aria-hidden', 'false');
      void this.fetchProduct();
    }

    hide({ restoreFocus = true } = {}) {
      const trigger = this.toggleElement;
      this.toggleElement = null;

      this.classList.add('closing');
      setTimeout(() => {
        this.wrapper?.classList.remove('sidebar--opened');
        this.classList.remove('closing', 'loaded');
        this.url = null;
        this.wrapper?.setAttribute('aria-hidden', 'true');
        this.setAttribute('aria-hidden', 'true');
        this.classList.remove('active');
        this.style.display = 'none';
        unlockQuickShopPageInert();

        if (
          !document.querySelector('custom-guide.active') &&
          !document.querySelector('custom-shop-the-look.active')
        ) {
          document.body.classList.remove('modal-open');
        }

        if (restoreFocus) {
          trigger?.focus?.({ preventScroll: true });
        }
      }, 300);
    }

    _onKeyDown(e) {
      if (!this.classList.contains('active')) return;
      if (this.wrapper?.getAttribute('aria-hidden') === 'true') return;
      if (isQuickShopCoveredByDrawer()) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        this.hide();
        return;
      }

      if (handleQuickShopGalleryKey(e)) return;

      trapTabInQuickShopDrawer(e, this.wrapper);
    }

    loaded() {
      this.loader?.classList.remove('working');
      this.classList.add('loaded');
      requestAnimationFrame(() => {
        this.wrapper?.classList.add('sidebar--opened');
        focusFirstInQuickShopDrawer(this.wrapper);
      });
    }

    // -- Fetching --

    async fetchProduct() {
      const cached = this.CACHE.get(this.url.href);
      if (cached && this.applySectionsResponse(cached)) {
        if (this.wrapperTitle) this.wrapperTitle.textContent = this.title;
        this.bindContent();
        this.loaded();
        return;
      }

      try {
        const response = await fetch(this.url, {
          headers: {'X-Requested-With': 'XMLHttpRequest'},
        });
        const content = await response.text();

        if (response.ok && this.applySectionsResponse(content)) {
          if (this.CACHE.size >= this.CACHE_LIMIT) {
            this.CACHE.delete(this.CACHE.keys().next().value);
          }
          this.CACHE.set(this.url.href, content);
        }
      } catch {
        // Intentionally silent
      } finally {
        if (this.wrapperTitle) this.wrapperTitle.textContent = this.title;
        this.bindContent();
        this.loaded();
      }
    }

    applySectionsResponse(raw) {
      if (!this.section || !this.content) return false;
      try {
        const html = JSON.parse(raw)[this.section];
        if (!html) return false;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const sectionEl = doc.querySelector('.shopify-section');
        if (!sectionEl) return false;
        this.content.innerHTML = sectionEl.innerHTML;
        return true;
      } catch {
        return false;
      }
    }

    // -- Binding --

    bindContent() {
      this.productVariants?.removeEventListener('variant:changed', this._onVariantChanged);
      this.productVariants?.removeEventListener('combined-listing:changed', this._onCombinedListingChanged);
      this.productForm?.removeEventListener('add-to-cart', this._onAddToCart);

      this.productVariants = this.content?.querySelector('product-variants');
      this.productForm = this.content?.querySelector('product-form');
      this.gallery = this.querySelector('.quick-shop-gallery .slides');
      this.priceOriginal = this.content?.querySelector('[data-js-product-price-original]');
      this.priceCompare = this.content?.querySelector('[data-js-product-price-compare]');
      this.finalSaleEl = this.content?.querySelector('[data-js-final-sale]');
      this.viewDetailsLink = this.content?.querySelector('[data-js-view-details]');
      this.bisForm = this.content?.querySelector('[data-js-bis-form]');
      this.bisVariantInput = this.bisForm?.querySelector('input[name="variant_id"]');

      if (this.productVariants) {
        this.productVariants.addEventListener('variant:changed', this._onVariantChanged);
        this.productVariants.addEventListener('combined-listing:changed', this._onCombinedListingChanged);
      }

      if (this.productForm) {
        this.productForm.addEventListener('add-to-cart', this._onAddToCart);
        this.injectAttribution();
      }

      // Apply initial variant state
      const variant = this.productVariants?.variant;
      if (variant?.id) this.handleVariantChanged({target: this.productVariants});
    }

    injectAttribution() {
      const form = this.productForm?.querySelector('form');
      if (!form) return;

      form.querySelectorAll('input[data-modal-attribution]').forEach((el) => el.remove());

      const url = window.location ? `${window.location.origin}${window.location.pathname}` : '';
      const trackingId = this.trackingId || this.toggleElement?.dataset?.trackingId || '';
      const attrs = {
        _modal_attribution_form: 'quick-shop',
        _modal_attribution_url: url,
        _modal_attribution_section_name: trackingId,
      };

      for (const [key, value] of Object.entries(attrs)) {
        if (!value) continue;
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${key}]`;
        input.value = value;
        input.setAttribute('data-modal-attribution', '');
        form.appendChild(input);
      }
    }

    // -- Modal product metadata --

    get modalProductData() {
      const json = this.productVariants?.querySelector('script.modal-product-data[type="application/json"]');
      if (!json) return null;
      try {
        return JSON.parse(json.textContent);
      } catch {
        return null;
      }
    }

    // -- Event handlers --

    handleAddToCart() {
      this.hide({ restoreFocus: false });
      setTimeout(() => {
        const cartSidebar = document.getElementById('site-cart-sidebar');
        if (KROWN.settings.cart_action === 'overlay' && cartSidebar) {
          cartSidebar.show();
        } else if (KROWN.settings.cart_popup === 'true') {
          const popup = document.getElementById('mini-cart-popup');
          if (popup) {
            popup.querySelector('[data-js-mini-cart-single]').style.display = 'block';
            popup.querySelector('[data-js-mini-cart-plural]').style.display = 'none';
            popup.show();
            if (popup.dataset.hide !== '0') {
              setTimeout(() => popup.hide(), parseInt(popup.dataset.hide));
            }
          }
        }
      }, 350);
    }

    handleVariantChanged(e) {
      const variant = e.target?.variant;
      if (!variant?.id) return;

      const productData = this.modalProductData;
      if (!productData) return;

      this.updatePrice(variant, productData);
      this.updateImage(variant);
      this.updateViewDetails(variant, productData);
      this.updateFinalSale(productData);
      this.updateTitle(productData);
      this.updateBisForm(variant);
    }

    handleCombinedListingChanged(e) {
      const pv = e.target;
      const sectionContent = pv.sectionContent;
      if (!sectionContent || !this.content) return;

      const sectionEl = sectionContent.querySelector('.shopify-section');
      if (!sectionEl) return;

      this.content.innerHTML = sectionEl.innerHTML;
      this.bindContent();
      this.loaded();

      // Update sidebar title from new variant data
      const newTitle = this.productVariants?.variant?.product_title;
      if (this.wrapperTitle && newTitle) this.wrapperTitle.textContent = newTitle;

      const targetId = pv.element?.dataset?.optionValueId;
      if (targetId) {
        requestAnimationFrame(() => {
          this.content.querySelector(`input[data-option-value-id="${targetId}"]`)?.focus();
        });
      }
    }

    // -- UI updates --

    updatePrice(variant, productData) {
      if (!this.priceOriginal || !this.priceCompare) return;

      const isFree = productData.free_product;
      const price = productData.price_varies ? variant.price : (productData.price ?? variant.price);
      const compareAt = productData.price_varies
        ? variant.compare_at_price
        : (productData.compare_at_price ?? variant.compare_at_price);

      if (isFree) {
        this.priceOriginal.textContent = 'FREE';
        const compareValue = compareAt > price ? compareAt : price;
        this.priceCompare.innerHTML = `<span>${theme.formatMoney(compareValue, KROWN.settings.shop_money_format)}</span>`;
        return;
      }

      this.priceOriginal.innerHTML = theme.formatMoney(price, KROWN.settings.shop_money_format);
      this.priceCompare.innerHTML = compareAt > price
        ? `<span>${theme.formatMoney(compareAt, KROWN.settings.shop_money_format)}</span>`
        : '';
    }

    updateImage(variant) {
      if (!this.gallery || !variant.featured_image_position) return;

      const target = this.gallery.querySelector(`[data-index="${variant.featured_image_position}"]`);
      if (!target) return;

      const isHorizontal = this.gallery.scrollWidth > this.gallery.clientWidth;
      this.gallery.scrollTo({
        left: isHorizontal ? target.offsetLeft - this.gallery.offsetLeft : 0,
        top: isHorizontal ? 0 : target.offsetTop - this.gallery.offsetTop,
        behavior: 'smooth',
      });
    }

    updateViewDetails(variant, productData) {
      if (!this.viewDetailsLink || !productData.url) return;
      const url = new URL(productData.url, window.location.origin);
      url.searchParams.set('variant', variant.id);
      this.viewDetailsLink.setAttribute('href', url.toString());
      this.viewDetailsLink.hidden = !!productData.free_product;
    }

    updateFinalSale(productData) {
      if (this.finalSaleEl) this.finalSaleEl.hidden = !productData.final_sale;
    }

    updateTitle(productData) {
      if (this.wrapperTitle && productData.title) this.wrapperTitle.textContent = productData.title;
    }

    updateBisForm(variant) {
      if (!this.bisForm) return;
      const showBis = !!variant && !variant.available;
      this.bisForm.hidden = !showBis;
      if (showBis && this.bisVariantInput) {
        this.bisVariantInput.value = variant.id;
        this.bisVariantInput.setAttribute('value', String(variant.id));
        this.bisVariantInput.dispatchEvent(new Event('change', {bubbles: true}));
      }
    }
  }

  customElements.define('product-modal', ProductModal);
}
